class ProtocolCartSummary extends HTMLElement {
  constructor() {
    super();
    this._config = {};
  }

  connectedCallback() {
    const configEl = this.querySelector('script[type="application/json"][data-cart-config]');
    if (configEl) {
      this._config = JSON.parse(configEl.textContent);
    }
    this._init();
  }

  _init() {
    // Bind event listeners
    this.addEventListener('click', this._handleClick.bind(this));
    // On load, remove any gift items that are no longer free
    fetch('/cart.js')
      .then((r) => r.json())
      .then((cartData) => {
        if (this._removeInvalidGifts(cartData)) return;
        // No invalid gifts — dispatch so other components (banners, modal) hydrate on load
        document.dispatchEvent(new CustomEvent('cart-data-updated', {
          bubbles: true,
          detail: { cartData }
        }));
      })
      .catch(() => null);
  }

  _handleClick(event) {
    const target = event.target.closest('[data-cart-quantity-increase], [data-cart-quantity-decrease], [data-cart-remove]');
    if (!target) return;

    const index = parseInt(target.dataset.index);
    if (!index) return;

    if (target.hasAttribute('data-cart-remove')) {
      this._removeItem(index);
    } else if (target.hasAttribute('data-cart-quantity-increase')) {
      this._updateQuantity(index, 1);
    } else if (target.hasAttribute('data-cart-quantity-decrease')) {
      this._updateQuantity(index, -1);
    }
  }

  refresh() {
    const sectionId = this._config.sectionId || this.dataset.sectionId;
    if (!sectionId) return;
    Promise.all([
      fetch(`${window.location.pathname}?sections=${sectionId}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      }).then((r) => r.json()),
      fetch('/cart.js').then((r) => r.json())
    ])
      .then(([sections, cartData]) => {
        if (this._removeInvalidGifts(cartData)) return;
        this._updateSections({ sections, item_count: cartData.item_count });
        document.dispatchEvent(new CustomEvent('cart-data-updated', {
          bubbles: true,
          detail: { cartData }
        }));
      })
      .catch((err) => console.warn('[ProtocolCartSummary] Refresh failed:', err));
  }

  _updateQuantity(line, change) {
    // Get current quantity from DOM
    const itemElement = this.querySelector(`[data-cart-item-id="CartItem-${line}"]`);
    if (!itemElement) return;

    const quantityElement = itemElement.querySelector('.protocol-cart-summary__quantity-value span, .protocol-cart-summary__item-col--quantity .protocol-cart-summary__quantity-value span');
    if (!quantityElement) return;

    const currentQuantity = parseInt(quantityElement.textContent.trim()) || 1;
    const newQuantity = Math.max(1, currentQuantity + change);

    if (newQuantity === currentQuantity && change < 0) {
      // Already at minimum, don't update
      return;
    }

    this._enableLoading(line);
    this._performCartUpdate(line, newQuantity);
  }

  _removeItem(line) {
    this._enableLoading(line);
    this._performCartUpdate(line, 0);
  }

  _performCartUpdate(line, quantity) {
    const body = JSON.stringify({
      line,
      quantity,
      sections: this._getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname
    });

    const routes = window.routes || {};
    const cartChangeUrl = routes.cart_change_url || `${window.Shopify?.routes?.root || '/'}cart/change.js`;
    
    fetch(cartChangeUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Cart update failed');
        }
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        if (parsedState.errors) {
          this._handleError(line, parsedState.errors);
          this._disableLoading(line);
          return;
        }

        // Remove gift items that are no longer free; if any removed, skip this cycle
        if (this._removeInvalidGifts(parsedState)) return;

        // Update sections using section rendering API — replaces DOM, clearing loader visually
        this._updateSections(parsedState);

        // Dispatch cart update event
        document.dispatchEvent(new CustomEvent('cart-data-updated', {
          bubbles: true,
          detail: { cartData: parsedState }
        }));
      })
      .catch((error) => {
        console.warn('[ProtocolCartSummary] Cart update failed:', error);
        this._handleError(line, 'Unable to update cart. Please try again.');
        this._disableLoading(line);
      });
  }

  _getSectionsToRender() {
    const sectionId = this._config.sectionId || this.dataset.sectionId;
    return [
      {
        id: `shopify-section-${sectionId}`,
        section: sectionId,
        selector: '.js-contents'
      }
    ];
  }

  _getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
  }

  _updateSections(parsedState) {
    if (!parsedState.sections) return;

    this._getSectionsToRender().forEach((sectionConfig) => {
      const sectionHTML = parsedState.sections[sectionConfig.section];
      if (!sectionHTML) return;

      // Update the main content area
      const elementToReplace = this.querySelector(sectionConfig.selector || '.js-contents');
      if (elementToReplace) {
        elementToReplace.innerHTML = this._getSectionInnerHTML(sectionHTML, sectionConfig.selector || '.js-contents');
      }

      // Update totals from the new section HTML (they're outside .js-contents)
      const html = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const protocolCartSummary = html.querySelector('protocol-cart-summary');
      if (protocolCartSummary) {
        // Update mobile total
        const mobileTotal = protocolCartSummary.querySelector('.protocol-cart-summary__total--mobile');
        const existingMobileTotal = this.querySelector('.protocol-cart-summary__total--mobile');
        if (mobileTotal && existingMobileTotal) {
          existingMobileTotal.innerHTML = mobileTotal.innerHTML;
        }

        // Update desktop total
        const desktopTotal = protocolCartSummary.querySelector('.protocol-cart-summary__total--desktop');
        const existingDesktopTotal = this.querySelector('.protocol-cart-summary__total--desktop');
        if (desktopTotal && existingDesktopTotal) {
          existingDesktopTotal.innerHTML = desktopTotal.innerHTML;
        }

        // Hide totals if cart is empty
        if (parsedState.item_count === 0) {
          const totalSections = this.querySelectorAll('.protocol-cart-summary__total');
          totalSections.forEach(section => {
            section.style.display = 'none';
          });
        } else {
          const totalSections = this.querySelectorAll('.protocol-cart-summary__total');
          totalSections.forEach(section => {
            section.style.display = '';
          });
        }
      }
    });

  }

  _formatMoney(cents) {
    // Use Shopify's money format if available
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents, window.money_format || '${{amount}}');
    }
    // Fallback: simple formatter
    const amount = (cents / 100).toFixed(2);
    const format = window.money_format || '${{amount}}';
    return format.replace('{{amount}}', amount);
  }

  _enableLoading(line) {
    // Both mobile and desktop items share the same line index
    this.querySelectorAll(`[data-cart-item-id="CartItem-${line}"]`).forEach(el => {
      el.style.opacity = '0.5';
      el.style.pointerEvents = 'none';
    });
  }

  _disableLoading(line) {
    this.querySelectorAll(`[data-cart-item-id="CartItem-${line}"]`).forEach(el => {
      el.style.opacity = '';
      el.style.pointerEvents = '';
    });
  }

  _removeInvalidGifts(cartData) {
    if (!cartData?.items) return false;
    const invalid = cartData.items
      .map((item, index) => ({ item, line: index + 1 }))
      .filter(({ item }) => item.properties?._gift_id && item.final_line_price > 0);
    if (invalid.length === 0) return false;
    // Remove one at a time — subsequent update cycles handle the rest (line numbers shift after each removal)
    this._performCartUpdate(invalid[0].line, 0);
    return true;
  }

  _handleError(_line, error) {
    console.warn('[ProtocolCartSummary] Error updating cart:', error);
    // You could show a toast notification here
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._handleClick.bind(this));
  }
}

export default ProtocolCartSummary;
